import { useLatest } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { getLayerAgentOperateConfig } from '@/utils/agent-teams-kit';
import { LayerType, PluginType, type IExportPlugin } from '@/version-types';
import { isProgressConfig, type LoadingExtraInfo } from '../../../shared';
import { BaseOperateView, BaseStagePlugin } from '../../components';
import {
    useActiveFrameIndex,
    useFramework,
    useLayerTranslate,
    useLoadingMap,
    useProgressMap,
    useScale,
} from '../../hooks';
import { LayerLoading } from './layer-loading';
import ProgressLoading from './progress-loading';
import { resolveCustomLoading } from './resolve-custom-loading';

const MIN_WIDTH_FOR_TEXT = 250;
const MIN_WIDTH_FOR_PROGRESS = 80;

const LoadingItem: React.FC<{ layerId: string; extraInfos?: LoadingExtraInfo[] }> = React.memo(
    ({ layerId, extraInfos }) => {
        const framework = useFramework();
        useLayerTranslate(layerId);
        const scale = useScale();
        const progressMap = useProgressMap();

        const layer = framework.service.flatLayer.getLayer(layerId);

        if (!layer) return null;

        let loadingNode: React.ReactNode = null;

        const customLoading = resolveCustomLoading(framework.service.behavior.get(layer), layer);
        if (customLoading.handled) {
            loadingNode = customLoading.node;
        } else {
            // 检查是否存在进度配置
            const progressConfig = extraInfos?.find((info) => isProgressConfig(info));
            const hasProgressConfig = !!progressConfig;
            const progress = progressMap[layerId] ?? 0;
            const scaleWidth = layer.width * scale;
            const borderRadius = (getLayerAgentOperateConfig(layer)?.style?.borderRadius || 0) * scale;

            // 浏览器的模糊跟元素的尺寸有关，这里基于设计稿的基准尺寸 252px 20px 计算模糊值
            const blur = (scaleWidth / 252) * 20;

            if (hasProgressConfig && isProgressConfig(progressConfig)) {
                // 渲染进度条 loading
                loadingNode = (
                    <ProgressLoading
                        progress={progress}
                        baseTime={progressConfig.baseTime}
                        blur={blur}
                        borderRadius={borderRadius}
                        isTextLayer={layer.layerType === LayerType.TEXT_LAYER}
                        hideText={scaleWidth < MIN_WIDTH_FOR_TEXT}
                        hideProgress={scaleWidth < MIN_WIDTH_FOR_PROGRESS}
                        textOnly={progressConfig.textOnly}
                    />
                );
            } else {
                // 渲染普通 loading
                loadingNode = (
                    <LayerLoading
                        blur={blur}
                        borderRadius={borderRadius}
                        isTextLayer={layer.layerType === LayerType.TEXT_LAYER}
                        hideIcon={scaleWidth < MIN_WIDTH_FOR_PROGRESS}
                    />
                );
            }
        }

        return <BaseOperateView layer={layer}>{loadingNode}</BaseOperateView>;
    },
);

/** 图层加载状态 */
const LayerLoadingPlugin: React.FC = React.memo(() => {
    const loadingMap = useLoadingMap();
    const layerIds = Object.keys(loadingMap);
    const activeStageIndex = useActiveFrameIndex();
    const framework = useFramework();
    const [_, refresh] = useState(0);
    const countRef = useLatest(layerIds.length);

    useEffect(() => {
        const refreshLoading = () => {
            if (countRef.current && countRef.current > 0) {
                refresh((prev) => prev + 1);
            }
        };
        const handle = framework.service.edgelessCanvas.onPan(refreshLoading);
        // 父组隐藏或恢复时，子图层的 loadingMap 不一定变化，需要重新判断整条祖先链。
        const layerHandle = framework.service.flatLayer.onChange(refreshLoading);
        return () => {
            framework.service.events.off(handle);
            framework.service.events.off(layerHandle);
        };
    }, [framework, countRef]);

    if (layerIds.length === 0) return null;

    const list = layerIds.filter((layerId) => {
        if (framework.service.flatLayer.checkIsNeedHidden(layerId)) return false;
        const obj = framework.service.edgelessCanvas.getByLayerId(layerId);
        if (!obj || obj?.checkInViewport().outOf) {
            return false;
        }

        const isSingleMode = framework.service.edgelessCanvas.isSingleMode;
        if (!isSingleMode) {
            return true;
        }

        const originIndex = framework.service.frame.getLayerFrameId(layerId);
        return originIndex === framework.service.frame.indexToFrameId(activeStageIndex);
    });

    if (list.length === 0) return null;

    return (
        <BaseStagePlugin style={{ zIndex: 1 }} name="layer-loading">
            {list.map((layerId) => {
                return <LoadingItem key={layerId} extraInfos={loadingMap[layerId]} layerId={layerId} />;
            })}
        </BaseStagePlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 20,
    name: 'layer-loading',
    render() {
        return <LayerLoadingPlugin />;
    },
};

export default exportPlugin;
